class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    /* 30/10/2026 (Kangaroo Hoodie, zone blanche au-dessus de « Il tuo carrello ») : la classe is-empty doit toujours
       refléter le rendu serveur. product-form.js retire is-empty dans son finally, même si l'ajout échoue ou si une
       réponse de suppression plus lente réinsère ensuite le bloc vide (.drawer__inner-empty, height:100%). Toute
       incohérence classe ↔ contenu déclenche un re-rendu depuis Shopify (source de vérité), jamais un masquage CSS. */
    new MutationObserver(() => this.checkEmptyState()).observe(this, { attributes: true, attributeFilter: ['class'] });
  }

  isEmptyMarkup() {
    return !!this.querySelector('.drawer__inner-empty');
  }

  isInconsistent() {
    const flagged = this.classList.contains('is-empty');
    return flagged !== this.isEmptyMarkup() || (flagged && !!this.querySelector('cart-drawer-items .cart-item'));
  }

  checkEmptyState() {
    if (this.refreshing || !this.isInconsistent()) return;
    /* cart.js bascule la classe AVANT de remplacer le HTML : on laisse passer ce rendu natif, puis on revérifie.
       Seule une incohérence qui persiste coûte une requête. */
    clearTimeout(this.emptyCheckTimer);
    this.emptyCheckTimer = setTimeout(() => { if (this.isInconsistent()) this.refreshFromServer(); }, 400);
  }

  async refreshFromServer() {
    if (this.refreshing) return;
    this.refreshing = true;
    try {
      const ids = this.getSectionsToRender().map((section) => section.id);
      const response = await fetch(`${window.routes?.cart_url || '/cart'}?sections=${ids.join(',')}`, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) return;
      const sections = await response.json();
      this.getSectionsToRender().forEach((section) => {
        const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
        const html = sections[section.id];
        if (!target || typeof html !== 'string') return;
        const nextHTML = this.getSectionInnerHTML(html, section.selector);
        if (nextHTML != null) target.innerHTML = nextHTML;
      });
      this.classList.toggle('is-empty', this.isEmptyMarkup());
      this.resetScroll();
    } catch (error) {
      console.error('[NuovaGlam cart drawer]', error);
    } finally {
      this.refreshing = false;
    }
  }

  resetScroll() {
    const items = this.querySelector('cart-drawer-items');
    if (items) items.scrollTop = 0;
    const inner = this.querySelector('.drawer__inner');
    if (inner) inner.scrollTop = 0;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.resetScroll();
    this.checkEmptyState();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    /* Réponse sans le rendu du tiroir (erreur, script tiers) : on relit Shopify au lieu d'afficher un état mélangé. */
    if (!parsedState.sections || typeof parsedState.sections['cart-drawer'] !== 'string') {
      this.refreshFromServer().then(() => this.open());
      return;
    }
    this.refreshing = true;

    this.getSectionsToRender().forEach((section) => {
      const html = parsedState.sections?.[section.id];
      if (!html) return;
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      const nextHTML = this.getSectionInnerHTML(html, section.selector);
      if (nextHTML != null) sectionElement.innerHTML = nextHTML;
    });

    const empty = this.isEmptyMarkup();
    this.classList.toggle('is-empty', empty);
    this.querySelector('.drawer__inner')?.classList.toggle('is-empty', empty);
    this.querySelector('cart-drawer-items')?.classList.toggle('is-empty', empty);
    this.resetScroll();
    this.refreshing = false;

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if(overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const node = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return node ? node.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
